import z, { type EnrichProps } from "zod";
import { getUnmanagedApplicationInsights } from "@packsize/application-monitoring";
import { type $ZodIssue, type ParseContext } from "zod/v4/core";

const MAXIMUM_PROPERTY_LENGTH = 8192;
const schemaMetadata = new WeakMap<z.ZodType, EnrichProps>();

const getSchemaMetadata = (schema: z.ZodType): EnrichProps => {
	return schemaMetadata.get(schema) ?? { callSite: "unknown" };
};

const logValidationError = (error: z.ZodError, metadata: EnrichProps) => {
	if (import.meta.env.DEV) {
		console.error("ZOD_VALIDATION_ERROR", error, metadata);
		return;
	}

	getUnmanagedApplicationInsights().trackException({
		exception: error,
		properties: {
			context: "ZOD_VALIDATION_ERROR",
			validationIssues: JSON.stringify(error.issues).slice(0, MAXIMUM_PROPERTY_LENGTH),
			errorCount: error.issues.length,
			...metadata,
			callSite: metadata.callSite || "unknown",
		},
	});
};

if (!z.ZodType.prototype.enrich) {
	z.ZodType.prototype.enrich = function (metadata: EnrichProps) {
		const wrappedSchema: typeof this = Object.create(this);
		schemaMetadata.set(wrappedSchema, metadata);
		wrappedSchema.safeParseWithTelemetry = z.ZodType.prototype.safeParseWithTelemetry.bind(wrappedSchema);
		wrappedSchema.safeParseWithTelemetryAsync = z.ZodType.prototype.safeParseWithTelemetryAsync.bind(wrappedSchema);
		wrappedSchema.parseWithTelemetry = z.ZodType.prototype.parseWithTelemetry.bind(wrappedSchema);
		wrappedSchema.parseWithTelemetryAsync = z.ZodType.prototype.parseWithTelemetryAsync.bind(wrappedSchema);
		return wrappedSchema;
	};

	z.ZodType.prototype.safeParseWithTelemetry = function (data: unknown, params?: ParseContext<$ZodIssue>) {
		const result = this.safeParse(data, { reportInput: true, ...params });

		if (result.success === false) {
			logValidationError(result.error, getSchemaMetadata(this));
		}

		return result;
	};

	z.ZodType.prototype.safeParseWithTelemetryAsync = async function (data: unknown, params?: ParseContext<$ZodIssue>) {
		const result = await this.safeParseAsync(data, { reportInput: true, ...params });

		if (result.success === false) {
			logValidationError(result.error, getSchemaMetadata(this));
		}

		return result;
	};

	z.ZodType.prototype.parseWithTelemetry = function (data: unknown, params?: ParseContext<$ZodIssue>) {
		try {
			return this.parse(data, { reportInput: true, ...params });
		} catch (error) {
			if (error instanceof z.ZodError) {
				logValidationError(error, getSchemaMetadata(this));
			}

			throw error;
		}
	};

	z.ZodType.prototype.parseWithTelemetryAsync = async function (data: unknown, params?: ParseContext<$ZodIssue>) {
		try {
			return await this.parseAsync(data, { reportInput: true, ...params });
		} catch (error) {
			if (error instanceof z.ZodError) {
				logValidationError(error, getSchemaMetadata(this));
			}

			throw error;
		}
	};
}

export const stringArrayWithoutNulls = z
	.array(z.any())
	.transform((maybeString) => maybeString.filter((a) => z.string().safeParse(a).success))
	.pipe(z.array(z.string()));
